<template>
    <div class="c-kod" :style="wrapStyle">
        <div :style="innerStyle">
            <div :style="headerStyle">
                <div :style="eyebrowStyle">{{ agencyName }}</div>
                <h1 :style="h1Style">{{ deal_name || 'Kick-off date' }}</h1>
                <p :style="leadStyle">
                    If you've started providing service, set the kick-off date.
                    Otherwise mark this deal as "Not started" or "Canceled".
                </p>
                <div v-if="totalPagesOfKickOff > 1" :style="pageMetaStyle">
                    Deal {{ currentPageKickOff }} of {{ totalPagesOfKickOff }}
                </div>
            </div>

            <div :style="contentRowStyle">
                <!-- Main column: kick-off type + nested datepicker + SOW upload -->
                <div :style="mainColStyle">
                    <CCard :extra-style="cardStyle">
                        <h3 :style="sectionTitleStyle">Kick-off status</h3>

                        <div :style="kickOffListStyle">
                            <div v-for="t in kick_off_types" :key="`type_${t.id}`">
                                <label
                                    :for="`type_${t.id}`"
                                    :style="typeChoiceStyle(upsell.kick_off_type === t.id)"
                                >
                                    <input
                                        type="radio"
                                        :id="`type_${t.id}`"
                                        v-model="upsell.kick_off_type"
                                        :checked="upsell.kick_off_type === t.id"
                                        name="type_id"
                                        :value="t.id"
                                        :style="srOnly"
                                        @change="onChangeKickOffType"
                                    />
                                    <span :style="typeChoiceDotStyle(upsell.kick_off_type === t.id)">
                                        <span v-if="upsell.kick_off_type === t.id" :style="typeChoiceInnerStyle" />
                                    </span>
                                    <div>
                                        <div :style="typeChoiceLabelStyle">{{ typeChoiceLabel(t.id) }}</div>
                                        <div :style="typeChoiceSubStyle">{{ typeChoiceSub(t.id) }}</div>
                                    </div>
                                </label>

                                <!-- Datepicker + SOW upload — only when "Service started" (id=1) is picked -->
                                <div v-if="t.id === 1 && upsell.kick_off_type === 1" :style="nestedBlockStyle">
                                    <div :style="fieldBlockStyle">
                                        <label :style="fieldLabelStyle">Kick-off date</label>
                                        <div class="form-field form-field--datepicker" ref="datepicker" :style="datepickerWrapStyle">
                                            <input
                                                type="text"
                                                id="kickoff_date"
                                                class="input-kick-off-date"
                                                v-model="upsell.kick_off_date"
                                                name="kickoff_date"
                                                placeholder="mm/dd/yyyy"
                                                :disabled="upsell.kick_off_type !== 1"
                                                :style="datepickerInputStyle"
                                                @changeDate="onChangeDatePicker"
                                            />
                                            <button type="button" class="btn btn--icon" :style="datepickerBtnStyle">
                                                <i class="icon icon-calendar"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div :style="signedSowWrapStyle">
                                        <label :style="signedSowLabelStyle">
                                            <input
                                                type="checkbox"
                                                id="have_sow_signed_sow"
                                                name="have_sow_signed_sow"
                                                :disabled="!upsell.kick_off_date"
                                                :checked="upsell.is_has_signed_sow"
                                                :style="srOnly"
                                                @change="upsell.is_has_signed_sow = !upsell.is_has_signed_sow"
                                            />
                                            <span :style="signedSowBoxStyle(upsell.is_has_signed_sow)">
                                                <span v-if="upsell.is_has_signed_sow">✓</span>
                                            </span>
                                            <span>I have signed SOW</span>
                                        </label>

                                        <div v-if="upsell.is_has_signed_sow" :style="sowTypesBlockStyle">
                                            <!-- File option -->
                                            <div :style="sowTypeRowStyle">
                                                <label :for="`sow_type_file`" :style="sowRadioLabelStyle">
                                                    <input
                                                        type="radio"
                                                        :id="`sow_type_file`"
                                                        v-model="upsell.selected_sow_type"
                                                        name="sow_type"
                                                        value="file"
                                                        :checked="upsell.selected_sow_type === 'file'"
                                                        :style="srOnly"
                                                        @change="onChangeSowType"
                                                    />
                                                    <span :style="typeChoiceDotStyle(upsell.selected_sow_type === 'file')">
                                                        <span v-if="upsell.selected_sow_type === 'file'" :style="typeChoiceInnerStyle" />
                                                    </span>
                                                    File
                                                </label>
                                                <div class="form-field form-field--file" ref="filepicker" :style="filePickerWrapStyle">
                                                    <input
                                                        type="file"
                                                        id="agreement"
                                                        name="agreement"
                                                        hidden
                                                        ref="fileInput"
                                                        :disabled="!upsell.is_sow_file"
                                                        @input="pickFile"
                                                        @change="pickFile"
                                                    />
                                                    <button
                                                        type="button"
                                                        class="btn btn--file"
                                                        :disabled="upsell.is_sow_file === null || !upsell.is_sow_file"
                                                        :style="filePickerBtnStyle(upsell.is_sow_file === null || !upsell.is_sow_file)"
                                                    >
                                                        Choose file +
                                                    </button>
                                                    <span class="filename" v-if="upsell.sow_filename" :style="filenameStyle">
                                                        {{ upsell.sow_filename }}
                                                    </span>
                                                </div>
                                            </div>

                                            <!-- URL option -->
                                            <div :style="sowTypeRowStyle">
                                                <label :for="`sow_type_url`" :style="sowRadioLabelStyle">
                                                    <input
                                                        type="radio"
                                                        :id="`sow_type_url`"
                                                        name="sow_type"
                                                        v-model="upsell.selected_sow_type"
                                                        value="url"
                                                        :checked="upsell.selected_sow_type === 'url'"
                                                        :style="srOnly"
                                                        @change="onChangeSowType"
                                                    />
                                                    <span :style="typeChoiceDotStyle(upsell.selected_sow_type === 'url')">
                                                        <span v-if="upsell.selected_sow_type === 'url'" :style="typeChoiceInnerStyle" />
                                                    </span>
                                                    URL
                                                </label>
                                                <div :style="urlInputWrapStyle">
                                                    <input
                                                        type="text"
                                                        id="sow_url"
                                                        v-model="upsell.sow_url"
                                                        name="sow_url"
                                                        placeholder="https://…"
                                                        :disabled="upsell.is_sow_file === null || upsell.is_sow_file"
                                                        :style="urlInputStyle"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CCard>
                </div>

                <!-- Right rail: service details -->
                <div :style="rightColStyle">
                    <CCard :extra-style="rightCardStyle">
                        <h3 :style="sidebarTitleStyle">Service details</h3>
                        <div :style="kvStackStyle">
                            <CKv v-if="upsell.services[0]" :k="'Service'" :v="getServiceName(upsell.services[0]) || '-'" />
                            <CKv v-if="upsell.custom_service" :k="'Custom service'" :v="upsell.custom_service || '-'" />
                            <CKv :k="'Type'" :v="getTypeName(upsell.type) || '-'" />
                            <CKv v-if="upsell.kickoff_date" :k="'Kick-off date'" :v="kickOffDateFormat(upsell.kickoff_date)" />
                            <CKv
                                v-if="upsell.type === 1 && upsell.mounthly_fee && !upsell.is_fluctuating_fee"
                                :k="'Monthly fee'"
                                :v="'$' + (numberWithCommas(upsell.mounthly_fee) || '-')"
                            />
                            <CKv
                                v-if="upsell.type === 1 && upsell.is_fluctuating_fee"
                                :k="'Fluctuating fee'"
                                :v="upsell.is_fluctuating_fee ? 'Yes' : 'No'"
                            />
                            <CKv
                                v-if="upsell.type === 1 && upsell.frequency_retainer"
                                :k="'Frequency'"
                                :v="Number(upsell.frequency_retainer) === 1 ? '1m' : '1y'"
                            />
                            <CKv
                                v-if="upsell.type === 2 && upsell.full_amount"
                                :k="'Full amount'"
                                :v="'$' + (numberWithCommas(upsell.full_amount) || '-')"
                            />
                            <CKv
                                v-if="upsell.type === 2 && upsell.number_of_pmts"
                                :k="'Number of pmts'"
                                :v="upsell.number_of_pmts || '-'"
                            />
                            <CKv
                                v-if="upsell.type === 3 && upsell.one_time_fee"
                                :k="'One-time fee'"
                                :v="'$' + (numberWithCommas(upsell.one_time_fee) || '-')"
                            />
                        </div>
                    </CCard>
                </div>
            </div>

            <div :style="navStyle">
                <CBtn ghost @click="onPrevSection">← Previous</CBtn>
                <span :style="navHintStyle">autosaved as you go</span>
                <CBtn primary big :disabled="isLoading" @click="onSaveNewUpsell">Next →</CBtn>
            </div>

            <div class="loader" v-if="isLoading" :style="loaderStyle">
                <clip-loader :loading="isLoading" color="#ec4657" size="45px" />
            </div>
        </div>
    </div>
</template>

<script>
import ClipLoader from "vue-spinner/src/ClipLoader";

import CurrencyInput from "../../../components/CurrencyInput";
import { Datepicker } from "vanillajs-datepicker";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import { SECTIONS } from "../consts";
import axios from "axios";
import moment from "moment";
import { scrollToTop } from "../../../helpers/_scrollToTop";

import CCard from "../c-design/CCard.vue";
import CBtn from "../c-design/CBtn.vue";
import CKv from "../c-design/CKv.vue";
import viewportMixin from "../c-design/useViewport";
import {
    C_BG,
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    C_GREEN,
    FONT_DISPLAY,
    FONT_BODY,
    navMobileStickyStyle,
} from "../c-design/tokens";

export default {
    name: "Wizard_Edit_KickOff_Date",
    mixins: [viewportMixin],
    props: ["onChangeSection"],
    data() {
        return {
            SECTIONS,
            isLoading: false,
            services_list: [],
            deal_name: "",
            brand_types: [
                {
                    id: 1,
                    name: "Retainer",
                },
                {
                    id: 2,
                    name: "Project",
                },
                {
                    id: 3,
                    name: "One-Time Fee",
                },
            ],
            kick_off_types: [
                {
                    id: 1,
                    name: "Kick-off date"
                },
                {
                    id: 2,
                    name: "Not started"
                },
                {
                    id: 3,
                    name: "Canceled"
                }
            ],
            upsell: {
                id: 0,
                services: [],
                kick_off_date: null,
                custom_service: "",
                terms: "",
                is_kick_off_canceled: false,
                kick_off_type: null,
                type: 1,
                one_time_fee: "",
                mounthly_fee: "",
                full_amount: "",
                number_of_pmts: "",
                is_fluctuating_fee: false,
                frequency_retainer: null,
                is_has_signed_sow: false,
                is_sow_file: null,
                sow_filename: "",
                sow_file: null,
                sow_url: "",
            },
            datepicker_options: {
                format: "mm/dd/yyyy",
                pickLevel: 0,
                autohide: true,
            },
            wizard_draft_sow_id: null,
            errors: {
                kick_off_type: null,
                type_sow: null,
                is_has_file: null,
                is_has_url: null,
                kick_off_date: null,
            },
            urlRegex:
                /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/,
            dealLineItemsWithoutKickOffDate: null,
            currentPageKickOff: 1,
            totalPagesOfKickOff: 1,
            is_create_custom_service: false,
            datePickerControl: null,
        };
    },
    components: {
        Treeselect,
        ClipLoader,
        CurrencyInput,
        CCard,
        CBtn,
        CKv,
    },

    created() {
        this.getData();
    },

    mounted() {
        this.initDatePicker();
        this.initFilePicker();


    },
    computed: {
        prevSection() {
            return this.$store.getters.getPrevComponent;
        },
        agencyName() {
            return this.$store.getters.getAgencyName;
        },
        currentBrand() {
            return this.$store.getters.getCurrentBrandName;
        },
        // ===== c-design style maps =====
        wrapStyle() {
            return {
                background: C_BG,
                minHeight: '70vh',
                padding: this.isMobile ? '14px 12px 110px' : '24px 16px 40px',
            };
        },
        innerStyle() {
            return { maxWidth: '1180px', margin: '0 auto' };
        },
        headerStyle() {
            return {
                marginBottom: this.isMobile ? '16px' : '24px',
                padding: this.isMobile ? '0 4px' : '0 8px',
            };
        },
        eyebrowStyle() {
            return {
                fontSize: '12px', color: C_MUTED, letterSpacing: '0.6px',
                textTransform: 'uppercase', fontWeight: 600,
            };
        },
        h1Style() {
            return {
                margin: '6px 0 8px', fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '24px' : '34px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-0.7px' : '-1px',
                lineHeight: 1.1, color: C_INK,
            };
        },
        leadStyle() {
            return { margin: 0, fontSize: '15px', color: C_MUTED, lineHeight: 1.55, maxWidth: '640px' };
        },
        pageMetaStyle() {
            return {
                marginTop: '12px', fontSize: '12px', color: C_MUTED,
                fontFamily: FONT_DISPLAY, fontWeight: 600,
                letterSpacing: '0.4px', textTransform: 'uppercase',
            };
        },
        contentRowStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) 320px',
                gap: '18px',
                alignItems: 'start',
            };
        },
        mainColStyle() { return {}; },
        rightColStyle() { return { position: 'sticky', top: '24px' }; },
        cardStyle() { return { marginBottom: '18px', padding: '28px 32px' }; },
        rightCardStyle() { return { padding: '24px' }; },
        sectionTitleStyle() {
            return {
                margin: '0 0 18px',
                fontFamily: FONT_DISPLAY,
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: C_MUTED,
            };
        },
        sidebarTitleStyle() {
            return {
                margin: '0 0 14px',
                fontFamily: FONT_DISPLAY,
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: C_MUTED,
            };
        },
        kvStackStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' };
        },
        kickOffListStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '10px' };
        },
        srOnly() {
            return {
                position: 'absolute', width: '1px', height: '1px',
                margin: '-1px', overflow: 'hidden',
                clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: '0',
            };
        },
        typeChoiceInnerStyle() {
            return {
                width: '10px', height: '10px', borderRadius: '50%',
                background: '#fff', display: 'inline-block',
            };
        },
        typeChoiceLabelStyle() {
            return {
                fontFamily: FONT_DISPLAY, fontSize: '15px', fontWeight: 700,
                color: 'inherit',
            };
        },
        typeChoiceSubStyle() {
            return {
                fontSize: '12px', color: 'inherit', opacity: 0.75, marginTop: '2px',
            };
        },
        nestedBlockStyle() {
            return {
                marginLeft: '0',
                marginTop: '8px',
                padding: '18px 18px 4px 18px',
                background: C_CREAM,
                borderRadius: '14px',
                borderLeft: `3px solid ${C_INK}`,
            };
        },
        fieldBlockStyle() { return { marginBottom: '14px' }; },
        fieldLabelStyle() {
            return {
                display: 'block', fontSize: '12px', color: C_INK2,
                fontWeight: 600, marginBottom: '6px',
            };
        },
        datepickerWrapStyle() {
            return {
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '10px',
                background: '#fff', padding: '0 8px 0 12px',
                maxWidth: '320px',
            };
        },
        datepickerInputStyle() {
            return {
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', padding: '10px 0',
                fontSize: '14px', fontFamily: FONT_BODY, color: C_INK, width: '100%',
            };
        },
        datepickerBtnStyle() {
            return {
                background: 'transparent', border: 'none',
                padding: '6px 4px', margin: '0 0 0 4px',
                fontSize: '16px', color: C_MUTED, cursor: 'pointer',
            };
        },
        signedSowWrapStyle() { return { marginTop: '8px' }; },
        signedSowLabelStyle() {
            return {
                display: 'flex', alignItems: 'center', gap: '10px',
                fontSize: '14px', color: C_INK2, fontWeight: 600,
                cursor: 'pointer',
            };
        },
        sowTypesBlockStyle() {
            return {
                marginTop: '14px',
                paddingTop: '14px',
                borderTop: `1px solid ${C_HAIR}`,
                display: 'flex', flexDirection: 'column', gap: '14px',
            };
        },
        sowTypeRowStyle() {
            return {
                display: 'grid', gridTemplateColumns: '120px 1fr', gap: '14px',
                alignItems: 'center',
            };
        },
        sowRadioLabelStyle() {
            return {
                display: 'flex', alignItems: 'center', gap: '10px',
                fontSize: '14px', color: C_INK2, fontWeight: 600,
                cursor: 'pointer',
            };
        },
        filePickerWrapStyle() {
            return {
                display: 'flex', alignItems: 'center', gap: '10px',
                flexWrap: 'wrap',
            };
        },
        filenameStyle() {
            return {
                fontSize: '13px', color: C_INK, fontFamily: FONT_DISPLAY,
                fontWeight: 600,
            };
        },
        urlInputWrapStyle() {
            return {
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '10px',
                background: '#fff', padding: '0 12px',
            };
        },
        urlInputStyle() {
            return {
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', padding: '10px 0',
                fontSize: '14px', fontFamily: FONT_BODY, color: C_INK, width: '100%',
            };
        },
        navStyle() {
            return navMobileStickyStyle(this.isMobile, {
                marginTop: '8px', padding: '16px 24px',
                background: '#fff', borderRadius: '16px',
                border: `1px solid ${C_HAIR}`,
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '16px',
            });
        },
        navHintStyle() {
            return { fontSize: '12px', color: C_MUTED, fontStyle: 'italic' };
        },
        loaderStyle() {
            return {
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: '32px',
            };
        },
    },
    methods: {
        // ===== c-design helpers =====
        typeChoiceStyle(selected) {
            return {
                textAlign: 'left',
                padding: '16px 18px',
                background: selected ? C_INK : '#fff',
                color: selected ? '#fff' : C_INK,
                border: `1.5px solid ${selected ? C_INK : C_HAIR}`,
                borderRadius: '14px',
                fontFamily: FONT_BODY,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                position: 'relative',
                transition: 'all 120ms',
            };
        },
        typeChoiceDotStyle(selected) {
            return {
                width: '20px', height: '20px', borderRadius: '50%',
                border: `1.5px solid ${selected ? '#fff' : C_HAIR}`,
                display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0,
            };
        },
        typeChoiceLabel(id) {
            return {
                1: 'Service started',
                2: 'Not started yet',
                3: 'Canceled',
            }[id] || `Type ${id}`;
        },
        typeChoiceSub(id) {
            return {
                1: 'Pick the kick-off date below',
                2: 'Service hasn\'t begun this period',
                3: 'Deal was canceled — no service',
            }[id] || '';
        },
        signedSowBoxStyle(checked) {
            return {
                width: '20px', height: '20px', borderRadius: '6px',
                border: `1.5px solid ${checked ? C_INK : C_HAIR}`,
                background: checked ? C_INK : '#fff',
                display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', color: '#fff',
                fontSize: '11px', fontWeight: 700, flexShrink: 0,
            };
        },
        filePickerBtnStyle(disabled) {
            return {
                padding: '10px 16px',
                background: disabled ? '#fff' : C_INK,
                color: disabled ? C_MUTED : '#fff',
                border: `1.5px solid ${disabled ? C_HAIR : C_INK}`,
                borderRadius: '10px',
                fontFamily: FONT_BODY, fontSize: '13px', fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
            };
        },

        async getData(is_update = false) {
            await axios
                .post("/api/wizard/getList", {
                    page: "edit_upsell",
                })
                .then((res) => {
                    this.dealLineItemsWithoutKickOffDate = res.data.page.deals;
                    this.services_list = res.data.page.services;
                    this.checkIfHaveDealLineItems(is_update);
                    this.$nextTick(() => {
                        scrollToTop(); // Прокрутка вверх
                    });
                })
                .catch((err) => console.log("err getData upsell", err));
        },
        initDatePicker() {
            const node__datepicker = this.$refs.datepicker;

            if (!node__datepicker.length) {
                return;
            }

            let datepicker = null;
            const input = node__datepicker[0].querySelector("input");
            const button = node__datepicker[0].querySelector("button");

            if (input) {
                datepicker = new Datepicker(input, this.datepicker_options);
                this.datePickerControl = datepicker;
            }

            if (button) {
                button.addEventListener("click", () => {
                    if (!datepicker.active) {
                        datepicker.show();
                    }
                });
            }
        },

        onChangeDatePicker(e) {
            const date = e.target.value;

            if (date) {
                this.upsell.kick_off_date = date;
            }
        },

        onChangeKickOffType() {

            switch (this.upsell.kick_off_type) {
                case 1:
                    this.upsell.is_kick_off_canceled = false;
                    this.upsell.kick_off_date = null;
                    this.datePickerControl.dates = [];
                    if (this.datePickerControl.pickerElement && this.datePickerControl.pickerElement.querySelector(".selected")) this.datePickerControl.pickerElement.querySelector(".selected").classList.remove('selected', 'focused')
                    break;
                case 2:
                    this.upsell.kick_off_date = null;
                    this.upsell.is_has_signed_sow = false;
                    this.datePickerControl.dates = [];
                    if (this.datePickerControl.pickerElement && this.datePickerControl.pickerElement.querySelector(".selected")) this.datePickerControl.pickerElement.querySelector(".selected").classList.remove('selected', 'focused')
                    break;
                case 3:
                    this.upsell.is_kick_off_canceled = true;
                    this.upsell.kick_off_date = null;
                    this.upsell.is_has_signed_sow = false;
                    this.datePickerControl.dates = [];
                    if (this.datePickerControl.pickerElement && this.datePickerControl.pickerElement.querySelector(".selected")) this.datePickerControl.pickerElement.querySelector(".selected").classList.remove('selected', 'focused')
                    break;
            }
        },

        onNextPage() {

            if (this.currentPageKickOff === this.totalPagesOfKickOff) {
                this.onChangeSection(SECTIONS.EDIT_SALE_LINE_ITEM, SECTIONS.EDIT_KICK_OFF_LINE_ITEM);
                return;
            }

            this.setCurrentLineItem(this.currentPageKickOff + 1);
            this.currentPageKickOff = this.currentPageKickOff + 1;

            this.$nextTick(() => {
                scrollToTop(); // Прокрутка вверх
            });
        },

        onPrevSection() {
            if (this.currentPageKickOff === 1) {
                this.onChangeSection(SECTIONS.DASHBOARD, SECTIONS.EDIT_KICK_OFF_LINE_ITEM);
                return;
            }
            this.setCurrentLineItem(this.currentPageKickOff - 1);
            this.currentPageKickOff = this.currentPageKickOff - 1;

            this.$nextTick(() => {
                scrollToTop(); // Прокрутка вверх
            });
        },

        onSaveNewUpsell() {

            this.validateFields();

            if (
                this.errors.kick_off_type ||
                this.errors.is_has_file ||
                this.errors.is_has_url ||
                this.errors.type_sow ||
                this.errors.kick_off_date
            ) {
                return;
            }

            this.isLoading = true;

            const data = {
                deal_line_item_id: this.upsell.id,
                kickoff_date: this.upsell.kick_off_date,
                is_kick_off_canceled: this.upsell.is_kick_off_canceled,
                kick_off_type: this.upsell.kick_off_type,
                is_has_signed_sow: this.upsell.is_has_signed_sow,
                is_sow_file: this.upsell.is_sow_file,
                sow_filename: this.upsell.sow_filename,
                sow_url: this.upsell.sow_url,
                selected_sow_type: null,
            };

            const formData = new FormData();

            formData.append("action", "updateDealLineItem");
            formData.append("data", JSON.stringify(data));
            formData.append(
                "file",
                this.upsell.sow_file === null ? "" : this.upsell.sow_file
            );

            axios
                .post("/api/wizard/updateDraft", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then(async () => {
                    //this.$toast.success("Upsell has been saved!");
                    this.getData(true);
                    this.onNextPage();

                })
                .catch((err) => {
                    this.$toast.error("Something went wrong!");
                    console.error(err);
                })
                .finally(() => (this.isLoading = false));
        },
        createRetainerSale() {

            const data = {
                deal_line_item_id: this.upsell.id
            }

            axios
                .post("/api/wizard/updateDraft", {
                    action: "createRetainerSale",
                    data: data,
                })
                .then(() => {
                    //this.$toast.success("Upsell has been saved!");
                    this.onNextPage();
                })
                .catch((err) => {
                    this.$toast.error("Something went wrong!");
                    console.error(err);
                })
                .finally(() => (this.isLoading = false));
        },
        validateFields() {

            this.errors.kick_off_type = null;
            this.errors.type_sow = null;
            this.errors.is_has_file = null;
            this.errors.is_has_url = null;
            this.errors.kick_off_date = null;

            if (!this.upsell.kick_off_type) {
                this.errors.kick_off_type = true;
                this.$toast.error("You should choose an answer")
            }

            if (this.upsell.kick_off_type === 1 && !this.upsell.kick_off_date) {
                this.errors.kick_off_date = true;
                this.$toast.error("You should choose a kick-off date")
            }

            if (this.upsell.is_has_signed_sow) {

                if (this.upsell.is_sow_file === null) {
                    this.errors.type_sow = true;
                    this.$toast.error("You should choose a type of SOW")
                } else {
                    if (this.upsell.is_sow_file && (!this.upsell.sow_file && !this.upsell.sow_filename)) {
                        this.errors.is_has_file = true;
                        this.$toast.error("You should upload SOW file")
                    } else if (!this.upsell.is_sow_file && !this.upsell.sow_url) {
                        this.errors.is_has_url = true;
                        this.$toast.error("You should enter SOW url")
                    } else if (!this.upsell.is_sow_file && this.upsell.sow_url && !this.urlRegex.test(this.upsell.sow_url)) {
                        this.errors.is_has_url = true;
                        this.$toast.error("Invalid URL");
                    }

                }

            }
        },

        checkIfHaveDealLineItems(is_update = false) {
            const deal_ids = Object.keys(this.dealLineItemsWithoutKickOffDate);

            if (deal_ids.length > 0) {

                this.totalPagesOfKickOff = Object.values(this.dealLineItemsWithoutKickOffDate).flat().length;

                if (!is_update) this.setCurrentLineItem(1);

            } else {

                this.onChangeSection(SECTIONS.EDIT_SALE_LINE_ITEM, SECTIONS.EDIT_KICK_OFF_LINE_ITEM)

            }
        },

        setCurrentLineItem(index) {

            if (Object.keys(this.dealLineItemsWithoutKickOffDate).length > 0) {

                const dealLineItems = Object.values(this.dealLineItemsWithoutKickOffDate).flat();

                this.upsell.id = dealLineItems[index - 1].deal_line_item_id;
                this.upsell.services = dealLineItems[index - 1].service_id;
                this.upsell.kick_off_date = dealLineItems[index - 1].kickoff_date;
                this.upsell.custom_service = dealLineItems[index - 1].custom_service;
                if (dealLineItems[index - 1].custom_service) {
                    this.is_create_custom_service = true;
                }
                this.upsell.type = dealLineItems[index - 1].type_id || dealLineItems[index - 1].commission_type_id;
                this.upsell.number_of_pmts = dealLineItems[index - 1].num_of_payments || dealLineItems[index - 1].payments_number;
                this.upsell.terms = dealLineItems[index - 1].terms;
                this.upsell.mounthly_fee =
                    dealLineItems[index - 1].type_id === 1 ? dealLineItems[index - 1].amount : "";
                this.upsell.full_amount = dealLineItems[index - 1].type_id === 2 ? dealLineItems[index - 1].amount : "";
                this.upsell.one_time_fee =
                    dealLineItems[index - 1].type_id === 3 ? dealLineItems[index - 1].amount : "";
                this.upsell.is_fluctuating_fee = dealLineItems[index - 1].fluctuating_fee;
                this.upsell.kick_off_type = dealLineItems[index - 1].hasOwnProperty("kick_off_type") ? dealLineItems[index - 1].kick_off_type : null;
                this.deal_name = dealLineItems[index - 1].deal_name || "",
                    this.upsell.frequency_retainer = dealLineItems[index - 1].frequency_retainer;

                this.upsell.sow_url = "";
                this.upsell.sow_filename = "";
                this.upsell.is_sow_file = null;
                this.upsell.selected_sow_type = null;
                this.upsell.sow_file = null;

                if (dealLineItems[index - 1].hasOwnProperty("is_has_signed_sow")) {
                    this.upsell.is_has_signed_sow = dealLineItems[index - 1].is_has_signed_sow;
                } else if (dealLineItems[index - 1].kickoff_date) {
                    this.upsell.is_has_signed_sow = true;
                } else {
                    this.upsell.is_has_signed_sow = false;
                }

                if (dealLineItems[index - 1].hasOwnProperty("is_sow_file")) {
                    if (dealLineItems[index - 1].is_sow_file) {
                        this.upsell.is_sow_file = true
                        this.upsell.selected_sow_type = "file";
                        this.upsell.sow_filename = dealLineItems[index - 1].sow_filename;
                    } else if (dealLineItems[index - 1].is_sow_file === null) {
                        this.upsell.is_sow_file = null
                    } else {
                        this.upsell.is_sow_file = false
                        this.upsell.selected_sow_type = "url";
                        this.upsell.sow_url = dealLineItems[index - 1].sow_url;
                    }
                }

                else if (dealLineItems[index - 1].hasOwnProperty("sow_url")) {
                    this.upsell.sow_url = dealLineItems[index - 1].sow_url;
                    this.upsell.selected_sow_type = "url";
                    this.upsell.is_sow_file = false
                }

                else if (dealLineItems[index - 1].hasOwnProperty("sow_filename")) {
                    this.upsell.sow_filename = dealLineItems[index - 1].sow_filename;
                    this.upsell.selected_sow_type = "file";
                    this.upsell.is_sow_file = true
                }

            }

        },

        getServiceName(id) {
            if (this.services_list.length) {

                const service = this.services_list.find(i => i.id === id);

                if (service) {
                    return service.label;
                }
            }

            return ""
        },

        getTypeName(id) {
            if (id) {

                const type = this.brand_types.find(i => i.id === id);

                if (type) {
                    return type.name;
                }
            }

            return ""
        },

        numberWithCommas(x) {
            if (!x) {
                return "0";
            }

            let fixednumber = Number(x).toFixed(2);
            let parts = fixednumber.split(".");

            let integerPart = parts[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")

            let formattedNumber = integerPart + "." + parts[1];

            return formattedNumber;
        },

        onChangeSowType(e) {

            const value = e.target.value;

            if (value === "file") {
                this.upsell.is_sow_file = true;
                this.upsell.sow_url = "";

            } else if (value === 'url') {
                this.upsell.sow_file = null;
                this.upsell.is_sow_file = false;
            }
            this.upsell.selected_sow_type = value;
        },

        pickFile() {
            let input = this.$refs.fileInput;
            let file = input.files;
            if (file && file[0]) {
                let reader = new FileReader();
                reader.readAsDataURL(file[0]);
                this.new_sow.file = file[0];
            }
        },

        initFilePicker() {
            const inputField = this.$refs.filepicker;

            if (!inputField[0]) {
                return;
            }

            const button = inputField[0].querySelector("button");
            const input = inputField[0].querySelector("input");
            const filenameNode = inputField[0].querySelector("span.filename");

            if (button && input) {
                button.addEventListener("click", () => {
                    input.click();
                });
            }

            input.addEventListener("change", () => {
                let file = input.files;
                if (file && file[0]) {
                    const filename = file[0].name;
                    this.upsell.sow_filename = filename;
                    //filenameNode.innerHTML = filename;
                    this.upsell.sow_file = file[0];
                }
            });
        },
        kickOffDateFormat(date = null) {
            if (date) {
                const formattedDate = moment(date).format('MM/DD/YYYY');

                return formattedDate;
            }

            return ""
        }
    },
};
</script>
